import { Component } from '@angular/core';
import { finalize } from 'rxjs';
import { ClienteUnicoServiceProxy, FidelidadOrderPointsDto } from '../services/service-proxy/service-proxies';
import { AppService } from '../services/app.service';

/** Un movimiento de puntos, ya listo para mostrar. */
interface Movimiento {
  fecha : Date | null;
  lugar : string;
  /** Total del movimiento: puntos por la compra + puntos por su valor. */
  puntos : number;
  puntosCompra : number;
  puntosPesos : number;
  porVencer : boolean;
  vence : Date | null;
}

@Component({
  selector: 'app-historial-puntos',
  templateUrl: './historial-puntos.component.html',
  styleUrls: ['./historial-puntos.component.scss']
})
export class HistorialPuntosComponent {

  isLoading = false;
  movimientos : Movimiento[] = [];
  cargado = false;
  error = false;

  constructor(private clienteUnicoService : ClienteUnicoServiceProxy,
              public appService : AppService) {}

  ngOnInit() {

    const idUnico = this.appService.idUnico!;

    this.isLoading = true;
    this.clienteUnicoService
        .getFidelidadPointsOrder(idUnico)
        .pipe(finalize(() => { this.isLoading = false; this.cargado = true; }))
        .subscribe({
          next : respuesta => this.movimientos = this.mapear(respuesta ?? []),
          // Un fallo (p. ej. el endpoint todavia sin [Authorize] desplegado) no
          // es lo mismo que "no hay movimientos": se muestra un aviso distinto.
          error : () => this.error = true
        });
  }

  private get esColombia() : boolean {
    return (this.appService.countryCode ?? 'AR').toUpperCase() === 'CO';
  }

  private mapear(datos : FidelidadOrderPointsDto[]) : Movimiento[] {
    return datos
      .map(d => {
        const fecha = this.aFecha(d.fecha);
        const puntosCompra = this.aNumero(d.puntosCompra);
        const puntosPesos = this.aNumero(d.puntosPesos);
        return {
          fecha,
          lugar : (d.lugar ?? '').trim(),
          puntosCompra,
          puntosPesos,
          // El total del movimiento es la suma de ambas bolsas.
          puntos : puntosCompra + puntosPesos,
          // La API marca con tipo="Por vencer" los puntos proximos a caducar.
          porVencer : (d.tipo ?? '').trim().toLowerCase() === 'por vencer',
          vence : this.vencimiento(fecha)
        };
      })
      .sort((a, b) => (b.fecha?.getTime() ?? 0) - (a.fecha?.getTime() ?? 0));
  }

  /**
   * Vencimiento derivado de la fecha de compra:
   *  - Argentina: exactamente un año después.
   *  - Colombia: un año después, redondeado al último día de ese mes (los
   *    puntos se computan a mes cerrado).
   */
  private vencimiento(compra : Date | null) : Date | null {

    if (!compra) return null;

    const v = new Date(compra.getTime());
    v.setFullYear(v.getFullYear() + 1);

    if (this.esColombia) {
      // Dia 0 del mes siguiente = ultimo dia del mes actual.
      return new Date(v.getFullYear(), v.getMonth() + 1, 0);
    }

    return v;
  }

  /** La API manda la fecha como "2026-08-22". */
  private aFecha(valor : string | undefined) : Date | null {
    if (!valor) return null;
    const d = new Date(valor);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Los puntos llegan como string ("175", o "1,234" con separador). Las
   * devoluciones vienen en negativo ("-1,094"): hay que conservar el signo
   * (quitar [^\d] a secas se comia el "-" y las dejaba en positivo).
   */
  private aNumero(valor : string | undefined) : number {
    const s = String(valor ?? '').trim();
    if (!s) return 0;
    const negativo = s.startsWith('-');
    const digits = s.replace(/[^\d]/g, '');
    if (!digits) return 0;
    const n = Number(digits);
    return isNaN(n) ? 0 : (negativo ? -n : n);
  }

  get hayMovimientos() : boolean {
    return this.movimientos.length > 0;
  }

  /** Desglose para el tooltip del total de puntos del movimiento. */
  desglose(m : Movimiento) : string {
    const compra = m.puntosCompra.toLocaleString('es-AR');
    const pesos = m.puntosPesos.toLocaleString('es-AR');
    return `${compra} puntos por tu compra y ${pesos} puntos por el valor de tu compra`;
  }

  /**
   * "De donde salio" el punto, como en el prototipo: "Compra online" cuando no
   * hay tienda, "Compra en <tienda>" cuando la hay.
   */
  concepto(m : Movimiento) : string {
    const lugar = m.lugar;
    // Puntos negativos = devolucion (la API manda los puntos en negativo).
    const verbo = m.puntos < 0 ? 'Devolución' : 'Compra';
    if (!lugar || /online/i.test(lugar)) return `${verbo} online`;
    return `${verbo} en ${lugar}`;
  }

  // ── Aviso de puntos por vencer ──────────────────────────────────────────────
  private get porVencer() : Movimiento[] {
    return this.movimientos.filter(m => m.porVencer);
  }

  get hayPorVencer() : boolean {
    return this.porVencer.length > 0;
  }

  get puntosPorVencer() : number {
    return this.porVencer.reduce((suma, m) => suma + m.puntos, 0);
  }

  /** La fecha de vencimiento mas proxima entre los puntos por vencer. */
  get proximoVencimiento() : Date | null {
    const fechas = this.porVencer
      .map(m => m.vence)
      .filter((f) : f is Date => f != null)
      .sort((a, b) => a.getTime() - b.getTime());
    return fechas[0] ?? null;
  }
}
